import {
    PresenceRemove,
    PresencesGrouped,
    PresenceUpdate,
    PresenceWebSocketResponses,
    UserPresence,
    UserPresences
} from '../Types/Types'
import { WebsocketMessageType } from '../../Common/API/Websockets/Types/Types'
import { Availability } from '../Constant/Constants'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

const mapPresencesForUpdate = (
    oldPresences: Nullable<PresencesGrouped>,
    updates: PresenceUpdate[]
): PresencesGrouped => ({
    ...updates.reduce(
        (accum: PresencesGrouped, next: PresenceUpdate): PresencesGrouped => {
            // Find the user in the grouped presences
            const matchingUser: Nullable<UserPresence> = Object.values(accum ?? {})
                .flat()
                .find((user: UserPresence): boolean => user.userID === next.userID)

            if (matchingUser) {
                // Remove the user from the grouped presences
                accum[matchingUser.presence.availability] = accum?.[matchingUser.presence.availability].filter(
                    (user: UserPresence): boolean => user.userID !== matchingUser.userID
                )

                // Remove the presence header if there are no users in that group
                if (accum?.[matchingUser.presence.availability]?.length === 0) {
                    delete accum[matchingUser.presence.availability]
                }

                if (isVarNotEmpty(accum?.[next.presence.availability])) {
                    accum[next.presence.availability].push({
                        ...matchingUser,
                        presence: next.presence
                    })
                } else {
                    accum[next.presence.availability] = [
                        {
                            ...matchingUser,
                            presence: next.presence
                        }
                    ]
                }
            } else if (isVarNotEmpty(accum?.[next.presence.availability])) {
                accum[next.presence.availability].push({
                    userID: next.userID,
                    name: next.name,
                    presence: next.presence
                })
            } else {
                accum[next.presence.availability] = [
                    {
                        userID: next.userID,
                        name: next.name,
                        presence: next.presence
                    }
                ]
            }

            return accum
        },
        oldPresences ?? ({} as PresencesGrouped)
    )
})

const mapPresencesForPaginate = (
    oldPresences: Nullable<PresencesGrouped>,
    newPresences: PresencesGrouped
): PresencesGrouped => ({
    ...Object.entries(newPresences ?? {}).reduce(
        (accum: PresencesGrouped, [key, value]: [string, UserPresences]): PresencesGrouped => {
            const keyAvailability: Availability = key as Availability

            return {
                ...accum,
                [keyAvailability]: [...(accum?.[keyAvailability] ?? []), ...value]
            }
        },
        oldPresences ?? ({} as PresencesGrouped)
    )
})

const mapPresencesForAdd = (
    oldPresences: Nullable<PresencesGrouped>,
    newPresences: PresencesGrouped
): PresencesGrouped => ({
    ...oldPresences,
    ...newPresences
})

const mapPresencesForDelete = (
    oldPresences: Nullable<PresencesGrouped>,
    presencesToRemove: PresenceRemove[]
): PresencesGrouped => ({
    ...Object.entries(oldPresences ?? {}).reduce(
        (accum: PresencesGrouped, [key, value]: [string, UserPresences]): PresencesGrouped => {
            const keyAvailability: Availability = key as Availability

            const removed: PresencesGrouped = {
                ...accum,
                [keyAvailability]: value.filter(
                    (user: UserPresence): boolean =>
                        !presencesToRemove.some((presence: PresenceRemove): boolean => presence.userID === user.userID)
                )
            }

            if (removed[keyAvailability].length === 0) {
                delete removed[keyAvailability]
            }

            return removed
        },
        {} as PresencesGrouped
    )
})

export const presenceWebsocketHandler = (
    oldPresences: Nullable<PresencesGrouped>,
    response: PresenceWebSocketResponses
): Nullable<PresencesGrouped> => {
    if (response.type === WebsocketMessageType.UPDATE) {
        return mapPresencesForUpdate(oldPresences, response.data.users)
    }

    if (response.type === WebsocketMessageType.REMOVE) {
        return mapPresencesForDelete(oldPresences, response.data.users)
    }

    if (response.type === WebsocketMessageType.ADD) {
        return mapPresencesForAdd(oldPresences, response.data.users)
    }

    if (response.type === WebsocketMessageType.CHANGE && response.data.startIndex > 0) {
        return mapPresencesForPaginate(oldPresences, response.data.users)
    }

    if (response.type === WebsocketMessageType.CREATE || response.type === WebsocketMessageType.CHANGE) {
        return { ...response.data.users }
    }

    return undefined
}
